'use client'

import { useState } from 'react'

import { lastEventId } from '@sentry/react'
import type { ErrorComponentProps } from '@tanstack/react-router'

import { purgeLocalCache } from '@/shared/lib/persistence'
import { Button } from '@/shared/ui/Button'

/**
 * Tela de erro da raiz do router.
 *
 * Renderiza **no lugar** do `RootLayout` e portanto **fora do `AppProviders`**:
 * nada aqui pode depender de sessão, react-query, nuqs ou qualquer contexto do
 * app — quando o que quebrou é o próprio provider, o hook derrubaria a tela de
 * erro junto e o aluno voltaria ao fallback cru do TanStack.
 *
 * Sem ela, o que aparece é o `ErrorComponent` padrão do router: "Something went
 * wrong!" com um botão "Show Error", em inglês, sem saída.
 */
export function AppErrorFallback({ error, info, reset }: ErrorComponentProps) {
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Já capturado pelos handlers do `createRoot`; aqui só lemos o id para o
  // aluno poder informar ao suporte. Não recapturamos, senão o evento duplica.
  const eventId = lastEventId()

  const message = error instanceof Error ? error.message : String(error)

  const handleClearAndReload = () => {
    purgeLocalCache()
    window.location.reload()
  }

  return (
    <main
      role="alert"
      className="flex min-h-screen w-full items-center justify-center bg-background px-4 py-10"
    >
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 text-center shadow-lg">
        <h1 className="text-xl font-bold text-primary-dark">
          Algo deu errado por aqui
        </h1>

        <p className="mt-3 text-sm font-medium text-gray-500">
          Não conseguimos carregar esta tela. Você pode tentar de novo — e, se o
          erro insistir, limpar os dados salvos no aparelho costuma resolver.
          Sua conta e seu progresso não são afetados.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button type="button" variant="primary" onClick={reset}>
            Tentar novamente
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleClearAndReload}
          >
            Limpar dados e recarregar
          </Button>
        </div>

        {eventId ? (
          <p className="mt-6 text-xs font-medium text-gray-400">
            Código do erro: <span className="font-mono">{eventId}</span>
          </p>
        ) : null}

        <button
          type="button"
          onClick={() => setDetailsOpen(open => !open)}
          className="mt-4 text-xs font-semibold text-gray-400 underline underline-offset-2"
        >
          {detailsOpen ? 'Ocultar detalhes técnicos' : 'Ver detalhes técnicos'}
        </button>

        {detailsOpen ? (
          <pre className="mt-3 max-h-64 overflow-auto rounded-xl bg-gray-100 p-3 text-left text-[11px] leading-relaxed whitespace-pre-wrap text-gray-600">
            {message}
            {info?.componentStack ? `\n${info.componentStack}` : ''}
          </pre>
        ) : null}
      </div>
    </main>
  )
}
